import React, { useState } from "react";
import { Mic, Keyboard, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const PREF_KEY = "leadersim_practice_mode";

/**
 * PracticeModeModal
 *
 * Props:
 *   scenario        — scenario object (title, persona_name) for display context
 *   loading         — bool, true while session is being created
 *   onConfirm(mode) — called with "voice" | "text" when user starts
 *   onClose()       — called when user dismisses
 */
export function PracticeModeModal({ scenario, loading = false, onConfirm, onClose }) {
  const saved = localStorage.getItem(PREF_KEY);
  const [mode, setMode] = useState(saved || "voice");
  const [remember, setRemember] = useState(!!saved);

  const handleStart = () => {
    if (remember) {
      localStorage.setItem(PREF_KEY, mode);
    } else {
      localStorage.removeItem(PREF_KEY);
    }
    onConfirm(mode);
  };

  const modes = [
    {
      id: "voice",
      icon: <Mic className="w-6 h-6" strokeWidth={1.75} />,
      title: "Voice Conversation",
      desc: "Speak naturally — just like a real conversation",
    },
    {
      id: "text",
      icon: <Keyboard className="w-6 h-6" strokeWidth={1.75} />,
      title: "Text Conversation",
      desc: "Type your responses at your own pace",
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-testid="practice-mode-modal"
    >
      <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl shadow-black/25 overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-2">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-orange-500 mb-1">
              How would you like to practice?
            </div>
            {scenario && (
              <div className="text-base font-bold text-slate-900 leading-snug">
                {scenario.title}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-slate-100 text-slate-400 transition-colors ml-3 shrink-0"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        {/* Mode cards */}
        <div className="px-6 pt-4 pb-3 space-y-3">
          {modes.map((m) => {
            const selected = mode === m.id;
            return (
              <button
                key={m.id}
                data-testid={`mode-${m.id}-btn`}
                onClick={() => setMode(m.id)}
                className={`w-full flex items-center gap-4 px-4 py-4 rounded-2xl border-2 text-left transition-all ${
                  selected
                    ? "border-orange-500 bg-orange-50 text-orange-900"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                <div
                  className={`p-2.5 rounded-xl shrink-0 ${
                    selected ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {m.icon}
                </div>
                <div>
                  <div className={`font-semibold text-sm ${selected ? "text-orange-900" : "text-slate-800"}`}>
                    {m.title}
                  </div>
                  <div className={`text-xs mt-0.5 ${selected ? "text-orange-700" : "text-slate-500"}`}>
                    {m.desc}
                  </div>
                </div>
                <div
                  className={`ml-auto w-4 h-4 rounded-full border-2 shrink-0 ${
                    selected ? "border-orange-500 bg-orange-500" : "border-slate-300"
                  }`}
                >
                  {selected && (
                    <div className="w-full h-full rounded-full flex items-center justify-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Remember preference */}
        <div className="px-6 pb-5">
          <label className="flex items-center gap-2.5 cursor-pointer group" data-testid="remember-preference-label">
            <input
              type="checkbox"
              data-testid="remember-preference-checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-orange-500 accent-orange-500 cursor-pointer"
            />
            <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
              Remember my preference
            </span>
          </label>
          {saved && (
            <div className="mt-1.5 ml-6.5 text-xs text-slate-400">
              Previously saved: <span className="capitalize font-medium text-slate-500">{saved}</span>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="px-6 pb-6">
          <Button
            data-testid="start-practice-btn"
            onClick={handleStart}
            disabled={loading}
            className="w-full h-12 rounded-2xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm shadow-md shadow-orange-200 transition-all"
          >
            {loading ? "Starting…" : "Start Practice"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * ChangeModeModal — compact inline modal to switch mode during a session
 *
 * Props:
 *   currentMode       — "voice" | "text"
 *   onConfirm(mode)   — called with new mode
 *   onClose()
 */
export function ChangeModeModal({ currentMode, onConfirm, onClose }) {
  const saved = localStorage.getItem(PREF_KEY);
  const [mode, setMode] = useState(currentMode);
  const [updatePref, setUpdatePref] = useState(false);

  const handleSwitch = () => {
    if (updatePref) {
      localStorage.setItem(PREF_KEY, mode);
    }
    onConfirm(mode);
  };

  const modes = [
    { id: "voice", icon: <Mic className="w-5 h-5" strokeWidth={1.75} />, label: "Voice" },
    { id: "text", icon: <Keyboard className="w-5 h-5" strokeWidth={1.75} />, label: "Text" },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      data-testid="change-mode-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-3xl w-full max-w-xs shadow-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-bold text-slate-900">Change Practice Mode</div>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-slate-100 text-slate-400"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        <div className="flex gap-3 mb-4">
          {modes.map((m) => {
            const sel = mode === m.id;
            return (
              <button
                key={m.id}
                data-testid={`change-mode-${m.id}-btn`}
                onClick={() => setMode(m.id)}
                className={`flex-1 flex flex-col items-center gap-2 py-4 rounded-2xl border-2 transition-all ${
                  sel
                    ? "border-orange-500 bg-orange-50 text-orange-800"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                }`}
              >
                <div className={`p-2 rounded-xl ${sel ? "bg-orange-500 text-white" : "bg-slate-100"}`}>
                  {m.icon}
                </div>
                <span className={`text-xs font-semibold ${sel ? "text-orange-800" : "text-slate-700"}`}>
                  {m.label}
                </span>
              </button>
            );
          })}
        </div>

        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input
            type="checkbox"
            data-testid="update-saved-pref-checkbox"
            checked={updatePref}
            onChange={(e) => setUpdatePref(e.target.checked)}
            className="w-3.5 h-3.5 rounded accent-orange-500 cursor-pointer"
          />
          <span className="text-xs text-slate-500">Update saved preference</span>
        </label>

        <Button
          data-testid="confirm-mode-change-btn"
          onClick={handleSwitch}
          className="w-full h-10 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm"
        >
          Switch to {mode === "voice" ? "Voice" : "Text"}
        </Button>
      </div>
    </div>
  );
}
